import { Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import ModalV2 from '/@/components/bayer/modal-v2';
import settings from '/@/core/settings';
import type { parseUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { objectEntries } from '/@/utils';
import { reverse } from '/@/utils/router';
import type { NewFieldOption } from '/@/views/fields/field-new-v2/components/options-selector';
import NewFieldModalCard from '../card';

const Step1 = ({
    selectedOption,
    setSelectedOption,
    userNodesFilteredByFieldview,
    createOwnUserField,
    selectedUserNodeId,
    trackEntrySource,
    setStep,
    bodyProps,
    actionsProps,
    onClose,
    open,
    returnUrl,
}: {
    selectedOption: 'draw' | 'upload' | 'import' | null;
    setSelectedOption: (selectedOption: 'draw' | 'upload' | 'import' | null) => void;
    userNodesFilteredByFieldview: ReturnType<typeof parseUserNode>[];
    createOwnUserField: boolean;
    selectedUserNodeId: number | null;
    trackEntrySource?: Parameters<typeof mpTrackEvent<'Field create began'>>['1']['entry_source'];
    setStep: (step: number) => void;
    bodyProps?: React.ComponentProps<typeof ModalV2>['bodyProps'];
    actionsProps?: React.ComponentProps<typeof ModalV2>['actionsProps'];
    onClose: () => void;
    open: boolean;
    /** Custom URL to return to after field creation. If not provided, uses current location. */
    returnUrl?: string;
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const options: Record<
        NewFieldOption,
        {
            title: string;
            description: string;
            disabled: boolean;
            dataTestId: string;
        }
    > = {
        import: {
            title: !createOwnUserField
                ? _('Solicitar al productor acceder a sus lotes de FieldView')
                : _('Conectarme con Fieldview'),
            description: !createOwnUserField
                ? _(
                      'Recibirá un mail para autorizar la conexión. Cuando lo haga, los lotes y los datos se sincronizan automáticamente en {{appName}}.',
                      { appName: (settings as any).app?.name || 'Cultivio' }
                  )
                : _('Puedes conectarte con Fieldview y sincronzar tus lotes en {{appName}}', {
                      appName: (settings as any).app?.name || 'Cultivio',
                  }),
            disabled: createOwnUserField ? false : userNodesFilteredByFieldview.length === 0,
            dataTestId: 'add-field-button-import',
        },
        draw: {
            title: _('Dibujar los lotes uno por uno'),
            description: _('Puedes dibujar un lote o marcar un punto en el mapa'),
            disabled: false,
            dataTestId: 'add-field-button-draw',
        },
        upload: {
            title: _('Subir archivo'),
            description: _('Puedes subir un archivo con el formato .kmz, .kml, .geojson o .shp.'),
            disabled: false,
            dataTestId: 'add-field-button-upload',
        },
    };

    const handleMethodSelection = (method: NewFieldOption) => {
        setSelectedOption(method);

        mpTrackEvent('Field create method selected', {
            method: method,
        });
    };

    const handleNext = async () => {
        const inviteFieldviewStep = selectedOption === 'import' && !createOwnUserField;
        if (selectedUserNodeId && !inviteFieldviewStep) {
            if (trackEntrySource)
                mpTrackEvent('Field create began', {
                    entry_source: trackEntrySource,
                    create_own_user_field: createOwnUserField,
                });
        }

        if (selectedUserNodeId && ['draw', 'upload'].includes(selectedOption || '')) {
            // Use custom returnUrl if provided, otherwise capture current URL
            const nextUrl = returnUrl
                ? encodeURIComponent(returnUrl)
                : encodeURIComponent(location.pathname + location.search);
            navigate(
                reverse('fields.NewField', {
                    qs: { userNodeId: selectedUserNodeId, option: selectedOption, next: nextUrl },
                })
            );
        }
        return setStep(2);
    };

    return (
        <ModalV2
            open={open}
            onClose={() => onClose()}
            title={
                createOwnUserField
                    ? _('Elegí cómo querés cargar lotes en {{appName}}', {
                          appName: (settings as any).app?.name || 'Cultivio',
                      })
                    : _('Elegí cómo querés agregar los lotes de los productores')
            }
            actions={
                <Button
                    variant='contained'
                    color='secondary'
                    onClick={handleNext}
                    disabled={!selectedOption}
                    data-testid='add-field-button-next'
                >
                    {_('Continuar')}
                </Button>
            }
            bodyProps={bodyProps}
            actionsProps={actionsProps}
            maxWidth='md'
        >
            {objectEntries(options).map(([key, value]) => (
                <NewFieldModalCard
                    key={key}
                    title={value.title}
                    description={value.description}
                    selected={selectedOption === key}
                    onClick={() => handleMethodSelection(key)}
                    disabled={value.disabled}
                    dataTestId={value.dataTestId}
                />
            ))}
        </ModalV2>
    );
};

export default Step1;
